import { Client, IFrame, IMessage, StompSubscription } from "@stomp/stompjs";
import {
  setConnected,
  setCurrentUser,
  markStompUnavailable,
  setDisconnected,
  recorderListUpdated,
  userUpdated,
  userGroupUpdated,
  videoShareUpdated,
  recorderGroupUpdated,
  updateNotiCount,
  reportUpdated,
  organizationUpdated,
  alertRuleListUpdated,
  alertListUpdated,
  firmwareUpgrade,
  setReconnectedDate,
  StompState,
} from "./stompSlice";
import { getCookie } from "cookies/cookie";
import { ACCESS_TOKEN } from "api/axiosInstance";
import { Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { StompMessageBody, StompMessageKind } from "./interface";
import { UserInfo } from "api/interfaces/userInterface.interface";
import {
  deleteNotice,
  updateNotice,
  updateReleaseVersion,
} from "redux/reducers/notices/noticeReducer";
import { mapViewEventUpdated, mapViewRecorderAdded } from "./stompMapSlice";
import {
  planUpdated,
  SUBSCRIPTION_WS_STATUS,
  subscriptionUpdated,
} from "./stompSubscriptionSlice";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";

let subscription: StompSubscription | null = null;
let connectedUser: UserInfo | null = null;
let stompSessionGeneration = 0;
let stompOperationChain: Promise<void> = Promise.resolve();
let lastRecoveryAt = 0;
/** 다음 onConnect를 재연결로 간주할지 (unavailable/일시 disconnect 후 복구) */
let pendingReconnectEmit = false;

// 직렬 큐: 실패해도 다음 작업은 이어지고, 반환 Promise는 reject를 그대로 전달한다.
const enqueueStompOperation = <T>(operation: () => Promise<T>): Promise<T> => {
  const run = stompOperationChain.then(operation);
  stompOperationChain = run.then(
    () => undefined,
    () => undefined
  );
  return run;
};

// switch 등 fire-and-forget 호출용 (unhandled rejection 방지) 콘솔로그를 찍기위한 function
const voidEnqueueStompOperation = (operation: () => Promise<unknown>): void => {
  void enqueueStompOperation(operation).catch((error) => {
    if (process.env.NODE_ENV !== "production") {
      console.error("STOMP operation failed:", error);
    }
  });
};

const RECOVERY_THROTTLE_MS = 10_000;
const DEFAULT_RECONNECT_DELAY_MS = 5000;

const isSessionExpiredError = (frame: IFrame): boolean => {
  const message = (frame.headers["message"] || "").toLowerCase();
  const body = (frame.body || "").toLowerCase();
  return (
    message.includes("session expired") || body.includes("session expired")
  );
};

export const stompMiddleware = ({
  dispatch,
  getState,
}: {
  dispatch: Dispatch;
  getState: () => { stomp: StompState };
}) => {
  const client = new Client({
    brokerURL: process.env.REACT_APP_WEBSOCKET_URL,
    reconnectDelay: DEFAULT_RECONNECT_DELAY_MS,
    heartbeatIncoming: 4000,
    heartbeatOutgoing: 4000,
  });

  const syncConnectHeaders = () => {
    const token = getCookie(ACCESS_TOKEN);
    if (token) {
      client.connectHeaders = {
        Authorization: `Bearer ${token}`,
      };
    }
  };

  const clearSubscription = () => {
    if (subscription) {
      subscription.unsubscribe();
      subscription = null;
    }
  };

  const markStompConnectionLost = (error?: unknown) => {
    if (error !== undefined) {
      console.error("STOMP connection error:", error);
    }
    pendingReconnectEmit = true;
    dispatch(markStompUnavailable());
  };

  const isStaleSession = (generation: number, user: UserInfo | null): boolean =>
    stompSessionGeneration !== generation || connectedUser !== user;

  const performDisconnect = async (expectReconnect = false): Promise<void> => {
    stompSessionGeneration += 1; //connect와 disconnect일때만 변경
    connectedUser = null;
    clearSubscription();
    client.reconnectDelay = 0;
    client.onConnect = () => {};
    // 탭 복귀 등 세션 재개만 재연결로 취급 (로그아웃은 false)
    pendingReconnectEmit = expectReconnect;
    dispatch(setDisconnected());

    try {
      if (client.active) {
        await client.deactivate();
      }
    } catch (error) {
      console.error("STOMP disconnect failed:", error);
      throw error;
    }
  };

  const performConnect = async (user: UserInfo): Promise<void> => {
    stompSessionGeneration += 1; //connect와 disconnect일때만 변경
    const generation = stompSessionGeneration;
    connectedUser = user;
    dispatch(setCurrentUser(user));

    try {
      client.reconnectDelay = DEFAULT_RECONNECT_DELAY_MS;
      syncConnectHeaders();
      client.onConnect = createConnectHandler(user);

      if (client.active) {
        await client.deactivate();
      }

      if (isStaleSession(generation, user)) {
        return;
      }

      client.activate();
    } catch (error) {
      if (!isStaleSession(generation, user)) {
        markStompConnectionLost(error);
      }
      throw error;
    }
  };

  const performReconnect = async (): Promise<boolean> => {
    const user = connectedUser;
    if (!user) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("STOMP reconnect skipped: no active session");
      }
      return false;
    }

    const generation = stompSessionGeneration;
    pendingReconnectEmit = true;

    try {
      client.reconnectDelay = 0;
      clearSubscription();

      if (client.active) {
        await client.deactivate();
      }

      //시작시 값과 다르면 다시 연결하지 않음
      if (isStaleSession(generation, user)) {
        return false;
      }

      client.reconnectDelay = DEFAULT_RECONNECT_DELAY_MS;
      syncConnectHeaders();
      client.onConnect = createConnectHandler(user);
      client.activate();
      return true;
    } catch (error) {
      if (!isStaleSession(generation, user)) {
        markStompConnectionLost(error);
      }
      throw error;
    }
  };

  const reconnectWithStoredUser = (): Promise<boolean> =>
    enqueueStompOperation(performReconnect);

  const recoverStompConnection = async () => {
    if (!connectedUser) {
      return;
    }

    const now = Date.now();
    if (now - lastRecoveryAt < RECOVERY_THROTTLE_MS) {
      return;
    }

    lastRecoveryAt = now;
    try {
      await reconnectWithStoredUser();
    } catch {
      // reconnect시에는 이미 markStompUnavailable 처리되어있음
    }
  };

  client.beforeConnect = () => {
    syncConnectHeaders();
    return Promise.resolve();
  };

  client.debug = (str) => {
    if (process.env.NODE_ENV === "production") {
      return;
    }
    console.log("=> ", str, new Date());
  };

  client.onStompError = (frame) => {
    if (frame.headers["message"]) {
      console.error("Broker reported error: " + frame.headers["message"]);
    }
    if (frame.body) {
      console.error("Additional details: " + frame.body);
    }

    if (isSessionExpiredError(frame)) {
      void recoverStompConnection();
    }
  };

  client.onDisconnect = () => {
    if (process.env.NODE_ENV !== "production") {
      console.log("onDisconnect");
    }
  };

  client.onWebSocketError = () => {
    if (connectedUser) {
      markStompConnectionLost();
    }
  };

  client.onWebSocketClose = () => {
    if (connectedUser) {
      markStompConnectionLost();
    }
  };

  const messageHandler = (message: IMessage) => {
    console.log("Message:" + message);
    const body = JSON.parse(message.body) as StompMessageBody;
    console.log("Message body:" + message.body, body);
    // 새로운 메시지가 수신되면 알람 카운트 증가
    if (body.payload.isBell) {
      dispatch(updateNotiCount("increase"));
    }

    switch (body.kind) {
      case StompMessageKind.RECORDER: {
        dispatch(recorderListUpdated(body.payload));
        if (body.payload.event === "ADD") {
          dispatch(mapViewRecorderAdded(body.payload));
        }
        break;
      }
      case StompMessageKind.CAMERA: {
        dispatch(recorderListUpdated(body.payload));
        break;
      }
      case StompMessageKind.RECORDER_GROUP: {
        dispatch(recorderGroupUpdated(body.payload));
        break;
      }
      case StompMessageKind.ASSIGN: {
        dispatch(alertListUpdated(body.payload));
        break;
      }
      case StompMessageKind.USER: {
        dispatch(userUpdated(body.payload));
        break;
      }
      case StompMessageKind.USER_GROUP: {
        dispatch(userGroupUpdated(body.payload));
        break;
      }
      case StompMessageKind.VIDEO_SHARE: {
        dispatch(videoShareUpdated(body.payload));
        break;
      }
      case StompMessageKind.REPORT: {
        dispatch(reportUpdated(body.payload));
        break;
      }
      case StompMessageKind.ORGANIZATION: {
        dispatch(organizationUpdated(body.payload));
        break;
      }
      case StompMessageKind.ALERT_RULE: {
        dispatch(alertRuleListUpdated(body.payload));
        break;
      }
      case StompMessageKind.SITE_NOTICE: {
        const { event, isDisplay, noticeId } = body.payload;

        if (event === "DELETE" || !isDisplay) {
          dispatch(deleteNotice(noticeId));
        } else {
          dispatch(updateNotice(body.payload));
        }
        break;
      }
      case StompMessageKind.RELEASE: {
        dispatch(updateReleaseVersion(body.payload));
        break;
      }
      case StompMessageKind.MAPVIEW: {
        dispatch(mapViewEventUpdated(body.payload));
        dispatch(alertListUpdated(body.payload));
        break;
      }
      case StompMessageKind.SUBSCRIPTION: {
        const { currentUser } = getState().stomp;
        const isSelf = body.payload.opEmail === currentUser?.email;
        if (
          body.payload.status !== SUBSCRIPTION_WS_STATUS.CONFIRMED ||
          !isSelf
        ) {
          dispatch(subscriptionUpdated(body.payload));
        }
        break;
      }
      case StompMessageKind.PLAN: {
        const { currentAccount, currentUser } = getState().stomp;
        const isCpMatch =
          currentAccount?.accountLevel === LEVEL_TYPE.CP &&
          currentAccount.accountId === body.payload.accountId;
        const isSubCpMatch =
          currentAccount?.accountLevel === LEVEL_TYPE.CP &&
          currentAccount?.parentLevel === LEVEL_TYPE.CP &&
          currentAccount?.parentAccountId === body.payload.accountId;
        const isEuMatch =
          currentAccount?.accountLevel === LEVEL_TYPE.EU &&
          currentAccount.accountId === body.payload.targetAccountId;

        const isSelf = body.payload.opEmail === currentUser?.email;

        if ((isCpMatch || isSubCpMatch || isEuMatch) && !isSelf) {
          dispatch(planUpdated(body.payload));
        }
        break;
      }
      case StompMessageKind.FIRMWARE: {
        dispatch(firmwareUpgrade(body.payload));
        break;
      }
      default: {
        return;
      }
    }
  };

  const createConnectHandler = (user: UserInfo) => {
    return () => {
      const isReconnect = pendingReconnectEmit;
      pendingReconnectEmit = false;
      dispatch(setConnected({ user, isReconnect }));
      setTimeout(() => {
        dispatch(setReconnectedDate(null));
      }, 1000);
      subscription = client.subscribe(`/topic/${user.email}`, messageHandler, {
        ack: "client",
      });
    };
  };

  return (next: Dispatch) => (action: PayloadAction<UserInfo>) => {
    switch (action.type) {
      case "connect": {
        voidEnqueueStompOperation(() => performConnect(action.payload));
        break;
      }
      case "reconnect": {
        voidEnqueueStompOperation(() => performReconnect());
        break;
      }
      case "disconnect": {
        const expectReconnect = !!(
          action.payload as { expectReconnect?: boolean } | undefined
        )?.expectReconnect;
        voidEnqueueStompOperation(() => performDisconnect(expectReconnect));
        break;
      }
      default: {
        return next(action);
      }
    }
  };
};
